'use client';
import { classNames } from '@repo/ui/src/utils/classNames';
import Section from '@repo/ui/src/components/Section';
import { buildImageSrc } from '@repo/ui/src/functions/image-functions-aspect-ratio';
import { HeaderVariationProps } from '@repo/ui/src/types/headerVariation';
import { AmplienceButtonProps } from '@repo/ui/src/types/button';
import { appConfig } from '../../utils/appConfig';
import TextContentColumn, { TextContentColumnProps } from './_Column';
import { ToutContent } from '@repo/ui/src/components/ToutContent';
import { MetaProps } from '@repo/ui/src/types/meta';
import Image from '@repo/ui/src/components/Image/New';
import { useIsMD } from '@repo/ui/src/utils/useMediaQuery';
import { ImageProps } from '@repo/ui/src/types';

export enum BackgroundTheme {
  Dark = 'Dark',
  Black = 'Black',
  DarkGray = 'Dark Gray',
}

export enum HeaderPosition {
  top = 'top',
  left = 'left',
}

type Align = 'left' | 'center' | 'right';

export interface Props {
  heading: string;
  description?: string;
  headerVariation: HeaderVariationProps;
  headerPosition: HeaderPosition;
  align: Align;
  darkTheme: boolean;
  headerDivider: boolean;
  noBackground: boolean;
  showLogImage: boolean;
  numColumns: number;
  midNumColumns: number;
  button: AmplienceButtonProps;
  columns: Array<TextContentColumnProps>;
  _meta: MetaProps;
  backgroundTheme: BackgroundTheme;
  showBackgroundTexture: boolean;
  _testID?: string;
  image?: ImageProps;
}

const TextContentColumns = ({
  heading,
  description,
  headerVariation,
  align,
  columns,
  darkTheme,
  button,
  headerPosition,
  headerDivider,
  numColumns = 3,
  midNumColumns,
  noBackground,
  showLogImage,
  _meta,
  showBackgroundTexture,
  backgroundTheme,
  _testID,
  image,
}: Props) => {
  const mdAndUp = useIsMD();
  const dark = backgroundTheme === BackgroundTheme.Dark || darkTheme || noBackground;
  const black = backgroundTheme === BackgroundTheme.Black;
  const darkGray = backgroundTheme === BackgroundTheme.DarkGray;
  const styledAsDarkTheme = dark || black || darkGray;
  const hasCornerImage = !!image?.image?.endpoint;
  const isTitledContent =
    _meta?.schema ===
    'https://traegergrills.com/data/contentTypes/titledContentMultipleColumns.json';
  const actuallyDisplayLogImage = showLogImage || (!dark && isTitledContent);

  const displayLargeLogImage = actuallyDisplayLogImage && columns.length > 3;

  if (!columns) return null;

  return (
    <Section
      gutterX
      gutterY
      basePadding
      dark={dark}
      black={black}
      headerPosition={headerPosition}
      texture={isTitledContent || showBackgroundTexture}
      darkGray={darkGray}
      data-test-id={`text_content_columns_${_testID}`}
      className={classNames(
        'overflow-hidden flex flex-col md:flex-row',
        headerPosition === HeaderPosition.top && 'flex-wrap justify-center',
        appConfig.isMeater && 'sm:py-32',
      )}
    >
      {actuallyDisplayLogImage && !hasCornerImage && (
        <div
          className={classNames(
            'absolute -z-10 -left-[20px] -top-[20px] scale-y-[-1] sm:-left-[50px] sm:-top-[50px] md:transform-none md:top-auto aspect-square',
            displayLargeLogImage
              ? 'max-w-[170px] sm:max-w-[340px] md:bottom-[50px] md:w-[500px]'
              : 'md:-bottom-[50px] w-[170px] md:w-[340px]',
          )}
        >
          <Image
            src={buildImageSrc(
              {
                endpoint: 'traeger',
                name: 'log-background',
              },
              500,
              0,
              0,
            )}
            role="presentation"
            alt=""
            width={500}
            height={500}
            className={classNames(styledAsDarkTheme && 'opacity-[0.15]')}
          />
        </div>
      )}
      {hasCornerImage && (
        <div
          className={classNames(
            'hidden md:block absolute -z-10 -left-[20px] -top-[20px] scale-y-[-1] sm:-left-[50px] sm:-top-[50px] md:transform-none md:top-auto aspect-square',
            displayLargeLogImage
              ? 'max-w-[170px] sm:max-w-[340px] md:bottom-[50px] md:w-[500px]'
              : 'md:-bottom-[50px] w-[170px] md:w-[340px]',
          )}
        >
          <Image
            src={buildImageSrc(image, 500, 0, 0)}
            role="presentation"
            alt=""
            width={500}
            height={500}
            className={classNames(styledAsDarkTheme && 'opacity-[0.15]')}
          />
        </div>
      )}
      <div
        className={classNames(
          'pl-6 pr-10 md:pb-4 flex-shrink-2 md:w-[35%]',
          headerPosition === HeaderPosition.top && heading && 'w-full',
          appConfig.isMeater && 'pt-0',
        )}
      >
        {heading && (
          <ToutContent
            headingStyleVariant="h2"
            forceDivider={headerDivider}
            buttons={button?.link?.link ? [button] : undefined}
            fullWidthDivider={appConfig.isMeater}
            dark={styledAsDarkTheme}
            align={mdAndUp ? align : 'left'}
            {...{ heading, headerVariation }}
          />
        )}
        {description && (
          <p
            className={classNames(
              'mt-4 font-normal',
              styledAsDarkTheme ? 'text-neutral-300' : 'text-neutral-700',
            )}
          >
            {description}
          </p>
        )}
      </div>
      <ul
        className={classNames(
          'list-none m-0 p-0',
          headerPosition === HeaderPosition.top ? 'w-auto' : 'w-full',
          'md:grid',
          midNumColumns
            ? `md:grid-cols-${midNumColumns} xl:grid-cols-${numColumns}`
            : `lg:grid-cols-${numColumns}`,
        )}
      >
        {columns.map((column: TextContentColumnProps) => (
          <li key={column.heading}>
            <TextContentColumn {...column} darkTheme={styledAsDarkTheme} />
          </li>
        ))}
      </ul>
    </Section>
  );
};

export default TextContentColumns;
